import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { BorderboxComponent, type BorderBoxState } from './borderbox.component';

@Component({
  selector: 'rcp-tile',
  template: `
    <rcp-borderbox [clickable]="clickable() ?? false" [draggable]="draggable() ?? false" [state]="computedState()">
      <div class="rcp-tile">
        <div class="tile-detail">
          <ng-content select="[leading-detail]" />
        </div>
        <div class="tile-content">
          <div class="tile-label text" [class.max-2-lines]="maxTwoLines()" [title]="label()">{{ label() }}</div>
          @if (description()) {
            <div class="tile-description text max-2-lines" [title]="description()">{{ description() }}</div>
          }
          <div class="tile-description hidden-if-empty">
            <ng-content select="[bottom-description]" />
          </div>
        </div>
        <div class="tile-actions global-component-style-flex-gap">
          <ng-content select="[trailing-detail]" />

          @if (showNavigatingChevron()) {
            <fa-icon [icon]="faChevronRight" class="tile-chevron" />
          }
        </div>

        <div class="tile-actions global-component-style-flex-gap tile-secondary-action">
          <ng-content select="[secondary-action]" />
        </div>
      </div>
    </rcp-borderbox>
  `,
  styleUrls: ['./tile.component.scss'],
  imports: [FontAwesomeModule, BorderboxComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class TileComponent {
  private readonly routerLink = inject(RouterLink, { optional: true });

  readonly clickable = input<boolean>();
  readonly draggable = input<boolean>();
  readonly label = input<string | number>();
  readonly description = input<string>();
  readonly disabled = input<boolean>();
  readonly highlighted = input<boolean>();

  readonly maxTwoLines = input(true);

  // use if tile is used for navigation, so in app we can show a chevron
  // don't use if the tile opens a modal
  readonly isNavigatingTile = input(false);

  readonly state = input<BorderBoxState>('default');

  // computed state to keep support for disabled and highlighted inputs
  readonly computedState = computed((): BorderBoxState => {
    if (this.disabled()) {
      return 'disabled';
    } else if (this.highlighted()) {
      return 'highlighted';
    }

    return this.state();
  });

  readonly showNavigatingChevron = computed(() => !!this.routerLink || this.isNavigatingTile());

  protected readonly faChevronRight = faChevronRight;
}
